import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { adminApiService } from "../services/api";

const useUpdateProduct = (productId, onProductUpdated, toggleEditProduct) => {
  // States
  const [isFeatured, setIsFeatured] = useState(false);
  const [isbestselling, setIsbestselling] = useState(false);
  const [isPercentage, setIsPercentage] = useState(false);
  const [productName, setProductName] = useState("");
  const [productDescription, setProductDescription] = useState("");
  const [productHighlightedText, setProductHighlightedText] = useState("");
  const [productSKU, setProductSKU] = useState("");
  // const [productImei, setProductImei] = useState("");
  const [productCategory, setProductCategory] = useState("");
  const [customerPrice, setCustomerPrice] = useState("");
  const [retailerPrice, setRetailerPrice] = useState("");
  const [distributorPrice, setDistributorPrice] = useState("");
  const [corporateClientPrice, setCorporateClientPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [productDiscount, setProductDiscount] = useState("");
  const [retailerPercentage, setRetailerPercentage] = useState("");
  const [distributorPercentage, setDistributorPercentage] = useState("");
  const [corporatePercentage, setCorporatePercentage] = useState("");
  const [specsField, setSpecsField] = useState([]);
  const [selectedSpecs, setSelectedSpecs] = useState([]);
  const [specInputs, setSpecInputs] = useState({});
  const [productImeiList, setProductImeiList] = useState("");
  const [productLinks, setProductLinks] = useState([""]);

  // Banners, product images, new image uploads
  const [bannerImages, setBannerImages] = useState([]);
  const [productimages, setProductImages] = useState([]);
  const [newAddedproductimages, setNewAddedproductimages] = useState([]);
  const [newAddedBannerimages, setNewAddedBannerimages] = useState([]);

  const [specifications, setSpecifications] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [colorImages, setColorImages] = useState({});
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchAllSpecsField();
  }, []);

  const fetchProductDetails = useCallback(async () => {
    try {
      const response = await adminApiService.getProductById(productId);
      if (!response.ok) throw new Error("Failed to fetch product details.");
      const responseJson = await response.json();
      const data = responseJson.product;

      const productSpec = responseJson.productSpec || {};

      setProductImeiList(
        responseJson.productImeiList.map((imei) => imei.imei_number).join(", ")
      );
      setProductName(data.name);
      setProductDescription(data.description);
      setProductHighlightedText(data.product_highlighted_text);
      setProductSKU(data.sku);
      setProductCategory(data.category_id);
      setCustomerPrice(data.price_customer);
      setQuantity(data.quantity);
      setIsPercentage(data.use_percentage);
      setRetailerPercentage(data.retailer_percentage || "");
      setDistributorPercentage(data.distributor_percentage || "");
      setCorporatePercentage(data.corporate_percentage || "");
      setProductDiscount(data.product_discount);
      setIsFeatured(data.isfeatured);
      setIsbestselling(data.isbestselling);
      setRetailerPrice(data.price_retailer || "");
      setDistributorPrice(data.price_distributor || "");
      setCorporateClientPrice(data.price_corporate || "");
      setBannerImages(data.banner_images || []);
      setSizes(data.sizes || []);
      setColors(data.colors || []);
      setProductImages(data.product_images || []);
      setProductLinks(data.product_links || [""]);
      // setColorImages(data.color_images || {});

      // Set already filled specifications as selected
      const filledSpecs = Object.keys(productSpec).filter(
        (spec) => productSpec[spec]
      );
      setSelectedSpecs(filledSpecs);
      setSpecInputs(productSpec);

      setSpecifications(
        Object.keys(productSpec).map((key) => ({
          spec: key,
          value: productSpec[key] || "",
        }))
      );
      //   setSpecInputs(Object.keys(productSpec));
    } catch (error) {
      console.log(error);
      toast.error("Failed to load product details.");
    }
  }, [productId]);

  useEffect(() => {
    if (productId) {
      fetchProductDetails();
    }
  }, [productId, fetchProductDetails]);

  const fetchCategories = async () => {
    try {
      const response = await adminApiService.getAllCategories();
      if (!response.ok) throw new Error("Failed to fetch categories.");
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      toast.error("Error fetching categories.");
    }
  };

  const fetchAllSpecsField = async () => {
    try {
      const response = await adminApiService.getProductSpecsFields();
      if (!response.ok) throw new Error("Failed to fetch specs field.");
      const data = await response.json();
      setSpecsField(data.columns || []);
    } catch (error) {
      toast.error("Error fetching specifications.");
    }
  };

  const handleColorImageChange = (e, color) => {
    if (!color) return;
    const files = Array.from(e.target.files).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setColorImages((prev) => ({
      ...prev,
      [color]: [...(prev[color] || []), ...files],
    }));
  };

  useEffect(() => {
    const colorsObj = colors.reduce((pre, acc) => {
      pre[acc] = productimages.filter((img) => img.startsWith(acc));
      return pre;
    }, {});

    console.log(colorsObj);

    setColorImages((prev) => ({
      ...prev,
      ...colorsObj,
    }));
  }, [colors, productimages]);

  const handleRemoveColorImage = (color, index) => {
    setColorImages((prev) => {
      const updatedArray = [...(prev[color] || [])];
      updatedArray.splice(index, 1);
      return {
        ...prev,
        [color]: updatedArray,
      };
    });
  };

  const handleNewProductImagesChange = (event) => {
    if (event.target.files) {
      const fileArray = Array.from(event.target.files).map((file) => ({
        file,
        url: URL.createObjectURL(file),
      }));

      setNewAddedproductimages((prevImages) => [...prevImages, ...fileArray]);
      setProductImages((prevImages) => [
        ...prevImages,
        ...fileArray.map((item) => item.url),
      ]);
    }
  };

  const removeProductImage = (index) => {
    const newImages = [...productimages];
    newImages.splice(index, 1);
    setProductImages(newImages);
  };

  const handleRemoveSpecification = (spec) => {
    setSelectedSpecs(selectedSpecs.filter((s) => s !== spec));
    const newSpecInputs = { ...specInputs };
    delete newSpecInputs[spec];
    setSpecInputs(newSpecInputs);
  };

  const handleAddSpecification = (e) => {
    const selectedSpec = e.target.value;
    if (selectedSpec && !selectedSpecs.includes(selectedSpec)) {
      // Add new specification to the list
      setSelectedSpecs((prevSpecs) => [...prevSpecs, selectedSpec]);
      setSpecInputs((prevInputs) => ({
        ...prevInputs,
        [selectedSpec]: "", // Initialize input for the new spec
      }));
    }
  };

  const handleSpecInputChange = (spec, value) => {
    // Update the specification input
    setSpecInputs((prevInputs) => ({
      ...prevInputs,
      [spec]: value,
    }));
  };

  const handleImeiChange = (e) => {
    const value = e.target.value;
    setProductImeiList(value);
  };

  const handlePercentageInput = () => setIsPercentage(!isPercentage);

  const handleNewBannerImagesChange = (event) => {
    if (event.target.files) {
      const fileArray = Array.from(event.target.files).map((file) => ({
        file,
        url: URL.createObjectURL(file),
      }));

      setNewAddedBannerimages((prevImages) => [...prevImages, ...fileArray]);
      setBannerImages((prevImages) => [
        ...prevImages,
        ...fileArray.map((item) => item.url),
      ]);
    }
  };

  const removeBannerImage = (index) => {
    const newImages = [...bannerImages];
    newImages.splice(index, 1);
    setBannerImages(newImages);
  };

  const closingModal = () => {
    resetFormFields();
    toggleEditProduct();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();

    formData.append("name", productName);
    formData.append("description", productDescription);
    formData.append("sku", productSKU);
    formData.append("category_id", productCategory);
    formData.append("price_customer", customerPrice);
    formData.append("use_percentage", isPercentage);
    formData.append("retailer_percentage", retailerPercentage);
    formData.append("distributor_percentage", distributorPercentage);
    formData.append("corporate_percentage", corporatePercentage);
    formData.append("quantity", quantity);
    formData.append("product_discount", productDiscount);
    formData.append("isfeatured", isFeatured);
    formData.append("isbestselling", isbestselling);
    formData.append("product_highlighted_text", productHighlightedText);

    // Append product links
    const filteredLinks = productLinks.filter((link) => link);
    formData.append("product_links", JSON.stringify(filteredLinks));

    if (!isPercentage) {
      formData.append("price_retailer", retailerPrice);
      formData.append("price_distributor", distributorPrice);
      formData.append("price_corporate", corporateClientPrice);
    }

    formData.append("sizes", sizes.join(","));
    formData.append("colors", colors.join(","));

    productimages.forEach((filename) =>
      formData.append("product_images", filename)
    );
    bannerImages.forEach((filename) =>
      formData.append("banner_images", filename)
    );
    newAddedproductimages.forEach((image) =>
      formData.append("new_product_images", image.file)
    );
    newAddedBannerimages.forEach((image) =>
      formData.append("new_banner_images", image.file)
    );

    Object.keys(colorImages).forEach((color) => {
      colorImages[color].forEach((imgObj) => {
        if (imgObj.existingFilename) {
          formData.append("product_images", imgObj.existingFilename);
        }
        if (imgObj.file) {
          formData.append(`color_images_${color}`, imgObj.file);
        }
      });
    });
    formData.append("product_imei_list", productImeiList);

    // Collect specifications and append to formData
    const specsArray = selectedSpecs.map((spec) => ({
      spec: spec,
      value: specInputs[spec] || "",
    }));
    formData.append("specifications", JSON.stringify(specsArray));

    try {
      //log the formdata
      for (let pair of formData.entries()) {
        console.log(pair[0] + ": " + pair[1]);
      }

      const response = await adminApiService.updateProduct(productId, formData);
      if (!response.ok) throw new Error("Network response was not ok");
      await response.json();
      toast.success("Product Updated successfully!");
      onProductUpdated();
      toggleEditProduct();
    } catch (error) {
      toast.error(`Failed to update product: ${error.message}`);
    }
  };

  const resetFormFields = () => {
    setIsFeatured(false);
    setIsbestselling(false);
    setProductName("");
    setProductDescription("");
    setProductHighlightedText("");
    setProductSKU("");
    // setProductImei("");
    setProductCategory("");
    setCustomerPrice("");
    setRetailerPrice("");
    setDistributorPrice("");
    setCorporateClientPrice("");
    setQuantity("");
    setProductDiscount("");
    setRetailerPercentage("");
    setDistributorPercentage("");
    setCorporatePercentage("");
    setProductImages([]);
    setBannerImages([]);
    setSizes([]);
    setColors([]);
    setColorImages({});
    setIsPercentage(false);
    setNewAddedproductimages([]);
    setNewAddedBannerimages([]);
    setProductLinks([""]);
  };

  return {
    isFeatured,
    setIsFeatured,
    isbestselling,
    setIsbestselling,
    isPercentage,
    setIsPercentage,
    productName,
    setProductName,
    productDescription,
    setProductDescription,
    productHighlightedText,
    setProductHighlightedText,
    productSKU,
    setProductSKU,
    productImeiList,
    setProductImeiList,
    productCategory,
    setProductCategory,
    customerPrice,
    setCustomerPrice,
    retailerPrice,
    setRetailerPrice,
    distributorPrice,
    setDistributorPrice,
    corporateClientPrice,
    setCorporateClientPrice,
    quantity,
    setQuantity,
    productDiscount,
    setProductDiscount,
    retailerPercentage,
    setRetailerPercentage,
    distributorPercentage,
    setDistributorPercentage,
    corporatePercentage,
    setCorporatePercentage,
    bannerImages,
    setBannerImages,
    productimages,
    setProductImages,
    newAddedproductimages,
    setNewAddedproductimages,
    newAddedBannerimages,
    setNewAddedBannerimages,
    selectedSpecs,
    setSelectedSpecs,
    specifications,
    setSpecifications,
    sizes,
    setSizes,
    colors,
    setColors,
    colorImages,
    setColorImages,
    categories,
    setCategories,
    productLinks,
    setProductLinks,
    handleSubmit,
    handleColorImageChange,
    handleRemoveColorImage,
    handleNewProductImagesChange,
    removeProductImage,
    handleRemoveSpecification,
    handleAddSpecification,
    handleSpecInputChange,
    handleImeiChange,
    handlePercentageInput,
    handleNewBannerImagesChange,
    removeBannerImage,
    closingModal,
    specsField,
    resetFormFields,
    specInputs,
  };
};

export default useUpdateProduct;
